import { getAuth } from 'firebase/auth';
import { initializeApp, getApps } from 'firebase/app';
import { getFirestore, collection, collectionGroup, getDocs, query, where, orderBy, doc, setDoc, getDoc, Timestamp, deleteDoc } from "firebase/firestore";
import { v4 as uuidv4 } from 'uuid';

// Environment-aware Firebase configuration
const getFirebaseConfig = () => {
  const isDevelopment = process.env.NODE_ENV === 'development';
  
  return {
    apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
    authDomain: isDevelopment 
      ? 'ask-the-oracle1.firebaseapp.com'  // Use Firebase default domain in development
      : process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN, // Use custom domain in production
    projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
    storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  };
};

const firebaseConfig = getFirebaseConfig();
const firebaseApp = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];

const db = getFirestore(firebaseApp);

export const firebaseAuth = getAuth(firebaseApp);

// ALL YOUR EXISTING DATABASE FUNCTIONS - UNCHANGED
export async function saveChat(userId, chatId, messages) {
  if (!userId || !chatId || !Array.isArray(messages)) {
    console.error("Invalid arguments for saveChat (userId or chatId missing)");
    return;
  }
  try {
    const chatDocRef = doc(db, "users", userId, "chats", chatId);
    
    const sanitizedMessages = messages.map(msg => ({
      id: msg.id || uuidv4(),
      role: msg.role,
      content: msg.content,
      createdAt: msg.createdAt instanceof Date ? Timestamp.fromDate(msg.createdAt) : Timestamp.now()
    }));

    const chatData = {
      chatId,
      messages: sanitizedMessages,
      updatedAt: Timestamp.now(),
    };

    const docSnap = await getDoc(chatDocRef);
    if (!docSnap.exists()) {
      chatData.createdAt = Timestamp.now();
    }

    await setDoc(chatDocRef, chatData, { merge: true });
  } catch (error) { 
    console.error(`Error saving chat ${chatId}: `, error);
  }
}

export async function getChatHistory(userId, chatId) {
  if (!userId || !chatId) {
      console.error("Missing userId or chatId for getChatHistory");
      return [];
  }
  try {
    const chatDocRef = doc(db, "users", userId, "chats", chatId);
    const docSnap = await getDoc(chatDocRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      const messagesWithDates = data.messages?.map(msg => ({
          ...msg,
          createdAt: msg.createdAt?.toDate()
      })) || [];
      return messagesWithDates; 
    } else {
      return [];
    }
  } catch (error) {
    console.error(`Error fetching chat history for ${chatId}: `, error);
    return [];
  }
}

export async function getUserChatSummaries(userId) {
  if (!userId) return [];
  try {
    const userChatsRef = collection(db, "users", userId, "chats");
    
    const q = query(
      userChatsRef,
      orderBy("updatedAt", "desc")
    );
    const querySnapshot = await getDocs(q);
    const chatList = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.chatId && data.messages && Array.isArray(data.messages)) {
          const firstUserMsg = data.messages.find(msg => msg.role === "user");
          const title = firstUserMsg 
              ? firstUserMsg.content.substring(0, 30) + (firstUserMsg.content.length > 30 ? "..." : "") 
              : "Consultation";
          chatList.push({
              id: doc.id, 
              chatId: doc.id,
              title,
              timestamp: data.updatedAt?.toDate().toISOString(),
              messages: data.messages || [],
          });
      }
    });
    return chatList;
  } catch (error) {
    console.error("Error fetching chat summaries for sidebar: ", error);
    return [];
  }
}

export async function deleteChat(userId, chatId) {
  if (!userId || !chatId) {
    console.error("Missing userId or chatId for deleteChat");
    throw new Error("Missing required parameters for deletion.");
  }
  try {
    const chatDocRef = doc(db, "users", userId, "chats", chatId);
    await deleteDoc(chatDocRef);
  } catch (error) { 
    console.error(`Error deleting chat ${chatId}: `, error);
    throw new Error("Failed to delete chat data.");
  }
}

export async function saveSpiritualAssessment(userId, assessmentData, options = {}) {
  if (!userId || !assessmentData) {
    console.error("Missing userId or assessmentData for saveSpiritualAssessment");
    return;
  }
  try {
    const userDocRef = doc(db, "users", userId);
    const { markCompleted = true } = options;
    const updatePayload = {
      spiritualAssessment: assessmentData,
    };

    if (markCompleted) {
      updatePayload.completedOnboarding = true;
      updatePayload.onboardingCompletedAt = new Date().toISOString();
    }

    // Ensure sessionVersion is initialized for global logout support
    // If user document doesn't exist or doesn't have sessionVersion, initialize it to 1
    const existingDoc = await getDoc(userDocRef);
    if (!existingDoc.exists() || !existingDoc.data()?.sessionVersion) {
      updatePayload.sessionVersion = 1;
    }

    await setDoc(
      userDocRef,
      updatePayload,
      { merge: true },
    );
  } catch (error) {
    console.error("Error saving spiritual assessment:", error);
    throw error;
  }
}

export { db };
